import { generatePath as generatePathBase } from 'react-router-dom';
import type { Route } from './routes';

export function generatePath<Path extends Route>(
  path: Path,
  params: PathParams<Path>,
  query?: Record<string, string | number | boolean | undefined | null>,
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const base = generatePathBase(path, params as any);

  if (query == null) {
    return base;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value != null) {
      searchParams.set(key, String(value));
    }
  }

  const search = searchParams.toString();
  return search.length > 0 ? `${base}?${search}` : base;
}

/**
 * Regex patterns for Hydra-only URLs that don't require loading an app bundle.
 *
 * When adding a new Hydra-only route:
 * 1. Add the route to main.tsx
 * 2. Add a regex pattern here to match the route
 *
 * Ember can check if a URL belongs to Hydra by calling window.__hydra__.isHydraRoute(path).
 */
const HYDRA_ONLY_URLS_REGEX = [
  /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i,
  /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/settings\/suppressions/i,
  /^\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/agent/i,
  /^\/workspaces\/(last|(\d+))\/tasks/i,
  /^\/workspaces\/(last|(\d+))\/analysis\/deliverability/i,
  /^\/workspaces\/(last|(\d+))\/health/i,
  /^\/workspaces\/(last|(\d+))\/home/i,
];

/**
 * Checks if a URL path matches a Hydra-only route.
 *
 * @example
 * isHydraRoute('/workspaces/123/agent') // true
 * isHydraRoute('/workspaces/last/settings/ai-business-profile') // true
 * isHydraRoute('/workspaces/123/foo') // false
 */
export function isHydraRoute(path: string): boolean {
  return HYDRA_ONLY_URLS_REGEX.some((regex) => regex.test(path));
}

/**
 * Magic types to extract path parameters from a route
 */
type ExtractPathParams<T extends string> = T extends `${infer _}:${infer Parameter}/${infer Rest}`
  ? Parameter | ExtractPathParams<Rest>
  : T extends `${infer _}:${infer Parameter}`
  ? Parameter
  : never;

type PathParams<T extends string> = {
  [K in ExtractPathParams<T>]: string | number;
};
